import {
  ActionIcon,
  Burger,
  Divider,
  Flex,
  Header as MantineHeader,
  MediaQuery,
  Text,
  Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import get from "lodash/get";
import Clock from "react-live-clock";

import { useXRayContext } from "../../../providers/XRayProvider.tsx";
import { CurrentUser } from "../../../types/user.ts";
import { DATE_FORMAT, TIMEZONE } from "../../../utils/date.ts";
import { theme } from "../../../utils/mantine/theme.ts";
import { toHumanReadable } from "../../../utils/string.ts";
import {
  getUserDisplayName,
  isBababosSuperAdmin,
} from "../../../utils/user.ts";
import SvgCalendar from "../../Icons/Calendar.tsx";
import SvgCrossedEye from "../../Icons/CrossedEye.tsx";
import HeaderLogo from "../../Icons/HeaderLogo.tsx";
import SvgUser from "../../Icons/User.tsx";
import SvgVisibility from "../../Icons/Visibility.tsx";
import MobileMenu from "../MobileMenu/MobileMenu.tsx";
import { TenantSelect } from "../TenantSelect.tsx";
import { useStyles } from "./Header.styles.ts";
import { QuickGuide } from "./QuickGuide.tsx";

interface HeaderProps {
  user: CurrentUser;
}

const Header = ({ user }: HeaderProps) => {
  const { classes } = useStyles();
  const [opened, { toggle }] = useDisclosure(false);
  const { xrayModeEnabled, setXrayModeEnabled, hasXrayModeAccess } =
    useXRayContext();

  const isSuperAdmin = isBababosSuperAdmin(user);

  return (
    <>
      <MantineHeader height={60} zIndex={1000}>
        <Flex
          justify="space-between"
          align="center"
          className={classes.wrapper}
        >
          <Flex gap={50}>
            <HeaderLogo />
            <QuickGuide />
          </Flex>

          <MediaQuery largerThan="52em" styles={{ display: "none" }}>
            <Burger opened={opened} onClick={toggle} color="white" />
          </MediaQuery>
          <MediaQuery smallerThan="52em" styles={{ display: "none" }}>
            <Flex gap={12} align="center" className={classes.info}>
              <Text>Welcome, {getUserDisplayName(user)}!</Text>
              <Divider orientation="vertical" className={classes.divider} />
              <>
                <SvgUser color={get(theme, "colors.golden.0")} />
                <Text color={get(theme, "colors.golden.0")}>
                  {toHumanReadable(user.role)}
                </Text>
              </>
              {isSuperAdmin && (
                <>
                  <Divider orientation="vertical" className={classes.divider} />
                  <TenantSelect />
                </>
              )}
              {hasXrayModeAccess && (
                <>
                  <Divider orientation="vertical" className={classes.divider} />
                  <Tooltip
                    label={
                      xrayModeEnabled
                        ? "Disable X-Ray feature"
                        : "Enable X-Ray feature"
                    }
                  >
                    <ActionIcon
                      size="md"
                      variant="outlineGolden"
                      onClick={() => {
                        setXrayModeEnabled(!xrayModeEnabled);
                      }}
                    >
                      {xrayModeEnabled ? <SvgCrossedEye /> : <SvgVisibility />}
                    </ActionIcon>
                  </Tooltip>
                </>
              )}
              <Divider orientation="vertical" className={classes.divider} />
              <>
                <SvgCalendar />
                <Clock
                  format={DATE_FORMAT}
                  ticking={true}
                  timezone={TIMEZONE}
                />
              </>
            </Flex>
          </MediaQuery>
        </Flex>
      </MantineHeader>
      {opened && <MobileMenu onClose={toggle} />}
    </>
  );
};

export default Header;
